/**
 * Main App Component
 * Application entry point with routing and authentication
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, useAuth } from './auth';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { NutSheet } from './pages/NutSheet';
import { Forecast } from './pages/Forecast';
import { Historical } from './pages/Historical';
import { Projections } from './pages/Projections';
import { AgentAccess } from './pages/AgentAccess';
import { Reports } from './pages/Reports';
import { Users } from './pages/Users';
import { Configuration } from './pages/Configuration';
import { Messages } from './pages/Messages';
import { Insurance } from './pages/Insurance';
import { canAccessInsurance, isInsuranceOnly, isMarketingManagerOnly, canAccessReports, isTeamServiceManager, isAccountingOnly, isFinanceManagerOnly, canAccessNutSheet, canViewConfig } from './auth/permissions';

/**
 * Protected Layout wrapper that provides user context
 */
const ProtectedLayout: React.FC = () => {
  const { user } = useAuth();
  return <Layout userName={user?.displayName} userRole={user?.role} />;
};

/**
 * Default route redirect based on user role
 * Insurance-only users go to /insurance, others go to /forecast
 */
const DefaultRedirect: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role || null;

  if (isInsuranceOnly(role)) {
    return <Navigate to="/insurance" replace />;
  }
  if (isAccountingOnly(role) || isFinanceManagerOnly(role)) {
    return <Navigate to="/nut-sheet" replace />;
  }
  if (isMarketingManagerOnly(role)) {
    return <Navigate to="/forecast" replace />;
  }
  // TSM always goes to Forecast
  return <Navigate to="/forecast" replace />;
};

/**
 * Guard that redirects TSM and MarketingManager users away from full dashboard pages
 */
const RequireDashboardFull: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const role = user?.role || null;
  if (isTeamServiceManager(role)) {
    return <Navigate to="/forecast" replace />;
  }
  if (isMarketingManagerOnly(role)) {
    return <Navigate to="/forecast" replace />;
  }
  if (isAccountingOnly(role) || isFinanceManagerOnly(role)) {
    return <Navigate to="/nut-sheet" replace />;
  }
  return <>{children}</>;
};

/**
 * Guard for the Nut Sheet route: allows Admin/CEO/Exec, GeneralManager, Accounting,
 * FinanceManager (F&I), and DepartmentManagers who hold NEW_VEHICLE and/or USED_VEHICLE.
 * The backend (resolveNutSheetAccess) is the real boundary and additionally scopes
 * DeptMgrs and FinanceManagers to their assigned locations.
 */
const RequireNutSheetAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, assignedDepartmentIds } = useAuth();
  const role = user?.role || null;
  if (!canAccessNutSheet(role, assignedDepartmentIds)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/**
 * Guard that keeps single-page Nut Sheet roles (Accounting, FinanceManager/F&I) on the
 * Nut Sheet page. Used on routes that are otherwise open to all authenticated users
 * (Forecast, Insurance).
 */
const BlockNutSheetOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const role = user?.role || null;
  if (isAccountingOnly(role) || isFinanceManagerOnly(role)) {
    return <Navigate to="/nut-sheet" replace />;
  }
  return <>{children}</>;
};

/**
 * Guard for the Configuration route: Admin/CEO/Exec (edit), GeneralManager
 * (view-only), and DepartmentManagers with NEW_VEHICLE — who see only the
 * Sales Teams tab (Configuration.tsx hides the rest; the backend config-write
 * gate is the real boundary).
 */
const RequireConfigAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, assignedDepartmentIds } = useAuth();
  const role = user?.role || null;
  if (!canViewConfig(role, assignedDepartmentIds)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/**
 * Guard for Reports page: allows dashboard users + MarketingManager
 */
const RequireReportsAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const role = user?.role || null;
  if (!canAccessReports(role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/**
 * Main application routes
 */
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public login route */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProtectedLayout />
          </ProtectedRoute>
        }
      >
        {/* Default redirect based on role */}
        <Route index element={<DefaultRedirect />} />

        {/* Forecast - accessible to all dashboard users including TSM (Accounting locked to Nut Sheet) */}
        <Route path="forecast" element={<BlockNutSheetOnly><Forecast /></BlockNutSheetOnly>} />

        {/* Nut Sheet - Admin/CEO/Exec, GM, Accounting, and DeptMgr w/ NEW or USED */}
        <Route
          path="nut-sheet"
          element={
            <RequireNutSheetAccess>
              <NutSheet />
            </RequireNutSheetAccess>
          }
        />
        <Route path="projections" element={<RequireDashboardFull><Projections /></RequireDashboardFull>} />
        <Route path="reports" element={<RequireReportsAccess><Reports /></RequireReportsAccess>} />
        <Route path="messages" element={<RequireDashboardFull><Messages /></RequireDashboardFull>} />

        {/* GM+ routes - hidden from DepartmentManager */}
        <Route
          path="historical"
          element={
            <ProtectedRoute requiredRole="GeneralManager">
              <Historical />
            </ProtectedRoute>
          }
        />
        {/* Configuration - GM+ see all tabs; NEW-dept DeptMgr sees Sales Teams only */}
        <Route
          path="configuration"
          element={
            <RequireConfigAccess>
              <Configuration />
            </RequireConfigAccess>
          }
        />

        {/* Executive+ routes - Users/Audit only */}
        <Route
          path="users"
          element={
            <ProtectedRoute requiredRole="Executive">
              <Users />
            </ProtectedRoute>
          }
        />

        {/* Agent Access - Admin, CEO, Executive (connect a private AI agent; backend enforces the same roles) */}
        <Route
          path="agent-access"
          element={
            <ProtectedRoute requiredRole="Executive">
              <AgentAccess />
            </ProtectedRoute>
          }
        />

        {/* Insurance Lead Queue - Insurance, Admin, Executive only (Accounting locked to Nut Sheet) */}
        <Route path="insurance" element={<BlockNutSheetOnly><Insurance /></BlockNutSheetOnly>} />

        {/* Catch-all redirect */}
        <Route path="*" element={<DefaultRedirect />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
